import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const AnnouncementManager = ({ onUpdate }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title_en: '',
    title_ti: '',
    content_en: '',
    content_ti: '',
    is_active: true
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await api.get('/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/announcements/${editingId}`, formData);
        toast.success('Announcement updated!');
      } else {
        await api.post('/admin/announcements', formData);
        toast.success('Announcement created!');
      }
      resetForm();
      fetchAnnouncements();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      title_en: announcement.title_en,
      title_ti: announcement.title_ti,
      content_en: announcement.content_en,
      content_ti: announcement.content_ti,
      is_active: announcement.is_active
    });
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const resetForm = () => {
    setFormData({
      title_en: '',
      title_ti: '',
      content_en: '',
      content_ti: '',
      is_active: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Manage Announcements</h2>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-announcement-btn"
        >
          {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {showForm ? 'Cancel' : 'New Announcement'}
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">{editingId ? 'Edit' : 'Create'} Announcement</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title (English)</label>
                <Input
                  value={formData.title_en}
                  onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                  required
                  placeholder="Enter English title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title (Tigrinya)</label>
                <Input
                  value={formData.title_ti}
                  onChange={(e) => setFormData({...formData, title_ti: e.target.value})}
                  required
                  placeholder="ትግርኛ ትርክ አክል"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Content (English)</label>
                <textarea
                  className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  value={formData.content_en}
                  onChange={(e) => setFormData({...formData, content_en: e.target.value})}
                  required
                  rows="4"
                  placeholder="Enter English content"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Content (Tigrinya)</label>
                <textarea
                  className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  value={formData.content_ti}
                  onChange={(e) => setFormData({...formData, content_ti: e.target.value})}
                  required
                  rows="4"
                  placeholder="ትግርኛ ትህትዋ አክል"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="mr-2"
              />
              <label htmlFor="is_active" className="text-sm font-medium">Active (show on website)</label>
            </div>

            <div className="flex space-x-3">
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button type="button" onClick={resetForm} variant="outline">
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Announcements List */}
      <div className="space-y-4">
        {loading ? (
          <p className="text-center py-8">Loading...</p>
        ) : announcements.length > 0 ? (
          announcements.map((announcement) => (
            <Card key={announcement.id} className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-bold">{announcement.title_en}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      announcement.is_active 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {announcement.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">{announcement.content_en}</p>
                  <p className="text-sm text-gray-500">Posted: {new Date(announcement.date).toLocaleDateString()}</p>
                </div>
                <div className="flex space-x-2 ml-4">
                  <Button
                    onClick={() => handleEdit(announcement)}
                    variant="outline"
                    size="sm"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(announcement.id)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">No announcements yet. Create your first one!</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AnnouncementManager;